/**
 * /well-testing/jobs/new — open a job (POST /well-testing/jobs,
 * fieldiq-api#1245). A full page, not a modal. `?wellId=` (from a well's page)
 * starts the form on that well. On save the job's own page replaces this one.
 *
 * WT_DISPATCH (admin, office staff, supervisor, dispatcher) only; any other
 * role is told so here rather than handed a form the API would refuse.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateWtJob, useWtMeta, useWtUsers, useWtWells } from '../../hooks/useWellTesting';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canDispatchWt, wtRefusal } from '../../lib/well-testing';
import { useBackToList } from '../downhole/recordPage';
import { JobForm } from './JobForm';
import { WT_EYEBROW, WtEmpty, WtLoading, WtPanel } from './WtBits';

export default function WtNewJobScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const back = useBackToList('/well-testing/jobs');
  const { user } = useAuth();
  const canAdd = canDispatchWt(user?.role);
  const meta = useWtMeta();
  const wells = useWtWells();
  const users = useWtUsers(canAdd);
  const create = useCreateWtJob();
  const [error, setError] = useState<string | null>(null);
  const metaFailure = queryLoadFailure(meta);
  const wellsFailure = queryLoadFailure(wells);

  return (
    <div data-testid="wt-new-job-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WT_EYEBROW} title="New job" description="Draft it now, or schedule it so the crew can log shifts." onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WtPanel eyebrow="Job register" title="Job">
            {!canAdd ? (
              <WtEmpty>Only an admin, office staff, a supervisor or a dispatcher can open a job.</WtEmpty>
            ) : meta.isLoading || wells.isLoading ? (
              <WtLoading label="Loading form" />
            ) : metaFailure || wellsFailure ? (
              <LoadFailureNotice
                error={queryFailureError(metaFailure ? meta : wells)}
                kind={(metaFailure ?? wellsFailure) || 'unreachable'}
                subject={metaFailure ? 'the job form' : 'your wells'}
              />
            ) : (wells.data?.data ?? []).length === 0 ? (
              <WtEmpty>A job is on a well, and there are no wells yet. Add one under Wells first.</WtEmpty>
            ) : (
              <JobForm
                job={null}
                initialWellId={searchParams.get('wellId') ?? undefined}
                catalog={meta.data?.data ?? null}
                meta={meta.data?.meta ?? null}
                users={users.data ?? []}
                pending={create.isPending}
                error={error}
                submitLabel="Create job"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success(res.data?.jobNumber ? `Job ${res.data.jobNumber} created` : 'Job created');
                      navigate(res.data?.id ? `/well-testing/jobs/${res.data.id}` : '/well-testing/jobs', { replace: true });
                    },
                    onError: (err) => setError(wtRefusal(err, 'The job could not be created.')),
                  });
                }}
              />
            )}
          </WtPanel>
        </div>
      </section>
    </div>
  );
}
